import { z } from 'zod';
import { ObjectId } from 'bson';

/** this is for mongodb ids ... we can accept string or objectId and it will make sure before inserting/updating to convert it to ObjectId */
export type Id = ObjectId | string;

/** this is an Object Id mainly used for Mongodb _id. If received objectId as a string, it change it to object id */
export const objectIdSchema = z.custom<ObjectId>().transform((id, ctx) => {
  // Nullish input is a plain validation failure — no noisy logging.
  // This path is hit constantly by introspection probes (e.g. zod-to-
  // json-schema's `isOptional()` calls `safeParse(undefined)` when
  // LangChain binds agent tools), so it must stay quiet.
  if (id == null) {
    ctx.addIssue({ code: 'custom', message: 'db_invalid_id' });
    return id;
  }
  try {
    // transform id string to object id
    id = getObjectId(id.toString());
  } catch (error) {
    console.log('id', id);
    console.error(error);
    console.log('ctx', ctx.path);
    ctx.addIssue({
      code: 'custom',
      message: 'db_invalid_id',
    });
  }

  return id;
});

/** a zod schema that checks if the id is ObjectId instance. It does NOT change any thing */
export const isObjectIdSchema = z.instanceof(ObjectId);

/**
 * Canonical schema for any **string id** that is NOT a MongoDB
 * `ObjectId` — e.g. the hex form of `_id` (`_id.toHexString()`), an
 * external system id, a national id number, an image asset id, a
 * one-signal device id, etc.
 *
 * Uses `z.coerce.string()` so accidental numeric inputs (e.g. `0`,
 * `123`) are coerced to their string form (`"0"`, `"123"`) instead of
 * being persisted as numbers or rejected outright. Trims surrounding
 * whitespace.
 *
 * **Use this — never raw `z.string()` — for every string-id field.**
 * Add `.min(1)`, `.optional()`, etc. as needed at the call site:
 *
 * ```ts
 * // ✅ correct
 * customerId: idSchema.min(1, 'Customer is required'),
 * registrationId: idSchema.optional(),
 *
 * // ❌ wrong — won't coerce, will reject `0` outright
 * customerId: z.string().min(1, 'Customer is required'),
 * ```
 */
export const idSchema = z.coerce.string().trim();

/**
 * Checks whether two ids refer to the same record.
 *
 * Either side may be `undefined` / `null` — callers across the app
 * already pass `?.` chains (`order.shipper?._id`, `leg.buyer?._id`,
 * …) so a strict signature is a lie that crashes in production.
 * Semantically, "is X the same id as Y?" is `false` when either side
 * is missing (two unknowns are not "the same id").
 *
 * If you genuinely need a presence check, do it explicitly:
 *
 * ```ts
 * if (a && b && isIdMatch(a, b)) { ... }
 * ```
 */
export const isIdMatch = (
  id: string | ObjectId | undefined | null,
  id2: string | ObjectId | undefined | null,
) => {
  if (id == null || id2 == null) return false;
  return id.toString() === id2.toString();
};

/**
 *  transform to or create new object Id
 * @param id objectId as string
 * @returns returns an objectId
 */
export const getObjectId = (id: string | ObjectId | undefined) => {
  return id ? new ObjectId(id.toString()) : new ObjectId();
};

/**
 * Canonical schema for a brand / website domain (`tryhero.io`,
 * `amazon.com`, `acme.co.uk`, …).
 *
 * Accepts a wide range of human inputs — the goal is "let the user paste
 * whatever's in their browser bar" — and normalises them to a bare host:
 *
 * - `tryhero.io`                                → `tryhero.io`
 * - `www.tryhero.io`                            → `tryhero.io`
 * - `https://tryhero.io`                        → `tryhero.io`
 * - `https://www.tryhero.io/`                   → `tryhero.io`
 * - `https://www.tryhero.io/pricing?ref=foo#x`  → `tryhero.io`
 * - `Tryhero.IO`                                → `tryhero.io`
 *
 * Validation is a strict label-by-label DNS check (lowercased,
 * alphanumeric + dashes, no leading/trailing dash, at least one dot,
 * each label 1-63 chars, host ≤ 253 chars). Bare hosts without a dot
 * (`localhost`, `intranet`) are rejected — domain == "publicly
 * resolvable name" for our use case (carrier-facing CRM).
 *
 * Use `.optional()` at call sites that allow clearing; the form layer
 * is responsible for sending `undefined` instead of `''` when the user
 * empties the field.
 */
const DOMAIN_LABEL_REGEX = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * Extract a clean host string from anything URL-ish the user might type
 * or paste. Returns `null` when the input doesn't look like it contains
 * a host at all (caller emits the validation issue).
 */
export function extractDomain(input: string): string | null {
  let s = input.trim().toLowerCase();
  if (!s) return null;
  // Strip scheme (http://, https://, ftp://, custom://, …) — anything
  // matching the RFC-3986 `scheme ":" "//"` shape. Built via `new RegExp`
  // so we don't have to escape the forward slashes (`no-useless-escape`).
  s = s.replace(new RegExp('^[a-z][a-z0-9+.-]*://'), '');
  // Strip user info (`user:pass@host`) if present — rare but legal.
  s = s.replace(/^[^@/]+@/, '');
  // Cut at the first path / query / fragment delimiter.
  s = s.split(/[/?#]/, 1)[0] ?? '';
  // Strip port (`example.com:8080` → `example.com`).
  s = s.replace(/:\d+$/, '');
  // Drop the trailing dot of a fully-qualified domain (`example.com.`).
  s = s.replace(/\.$/, '');
  // Drop leading `www.` (a presentation prefix, never the canonical host).
  s = s.replace(/^www\./, '');
  return s || null;
}

function isValidDomain(host: string): boolean {
  if (host.length === 0 || host.length > 253) return false;
  const labels = host.split('.');
  if (labels.length < 2) return false;
  for (const label of labels) {
    if (!DOMAIN_LABEL_REGEX.test(label)) return false;
  }
  return true;
}

export const domainSchema = z.coerce
  .string()
  .transform((raw, ctx) => {
    const host = extractDomain(raw);
    if (!host || !isValidDomain(host)) {
      ctx.addIssue({ code: 'custom', message: 'invalid_domain' });
      return z.NEVER;
    }
    return host;
  });
